import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '../lib/cn'
import { SearchIcon } from './icons'

const fieldBase =
  'w-full border border-DEFAULT bg-surface-muted text-[14px] text-gray-900 ' +
  'placeholder:text-gray-500 hover:border-strong ' +
  'focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 ' +
  'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500'

const invalidStyles = 'border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500/20'

type InputProps = InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(fieldBase, 'h-10 rounded-full px-4', invalid && invalidStyles, className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  )
})

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, rows = 4, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        fieldBase,
        'rounded-md px-4 py-3 leading-relaxed resize-y',
        invalid && invalidStyles,
        className,
      )}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  )
})

type SelectProps = SelectHTMLAttributes<HTMLSelectElement>

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cn(fieldBase, 'h-10 rounded-full pl-4 pr-9', className)} {...rest}>
      {children}
    </select>
  )
})

type SearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  wrapperClassName?: string
}

/** The pill search field with the magnifier baked in. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className, wrapperClassName, ...rest },
  ref,
) {
  return (
    <label
      className={cn(
        'flex h-10 items-center gap-2.5 rounded-full border border-DEFAULT bg-surface-muted px-4 text-gray-500',
        'focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20',
        wrapperClassName,
      )}
    >
      <SearchIcon size={16} />
      <input
        ref={ref}
        type="search"
        className={cn(
          'w-full bg-transparent text-[14px] text-gray-900 placeholder:text-gray-500 focus:outline-none',
          className,
        )}
        {...rest}
      />
    </label>
  )
})
